import React, { useMemo, useState } from 'react'
import AgentThreadMenu from './AgentThreadMenu'
import {
  agentColors,
  formatRelativeTime,
  glassRailStyle,
  sortConversationsRecentFirst,
} from './agentChatLayout'

function ThreadSkeletonRows({ count = 4 }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          style={{
            marginBottom: 4,
            minHeight: 52,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'rgba(255,255,255,0.35)',
            border: '1px solid rgba(255,255,255,0.45)',
          }}
        >
          <div
            style={{
              height: 12,
              width: `${70 + (i % 3) * 10}%`,
              borderRadius: 4,
              background: 'rgba(255,255,255,0.5)',
              marginBottom: 6,
            }}
          />
          <div
            style={{
              height: 10,
              width: '35%',
              borderRadius: 4,
              background: 'rgba(255,255,255,0.35)',
            }}
          />
        </div>
      ))}
    </>
  )
}

export default function AgentThreadList({
  conversations,
  activeId,
  onSelect,
  onNewChat,
  onRename,
  onDelete,
  mobile = false,
  loading = false,
}) {
  const [menuId, setMenuId] = useState(null)
  const [renamingId, setRenamingId] = useState(null)
  const [renameValue, setRenameValue] = useState('')
  const sorted = useMemo(() => sortConversationsRecentFirst(conversations), [conversations])

  const startRename = (conv) => {
    setRenamingId(conv.id)
    setRenameValue(conv.title || '')
    setMenuId(null)
  }

  const commitRename = async (id) => {
    const t = renameValue.trim()
    setRenamingId(null)
    if (t) await onRename(id, t)
  }

  const renderRow = (conv) => {
    const active = conv.id === activeId
    const showMenu = menuId === conv.id

    return (
      <div
        key={conv.id}
        style={{
          position: 'relative',
          marginBottom: 4,
          minHeight: 52,
        }}
      >
        {renamingId === conv.id ? (
          <input
            autoFocus
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={() => commitRename(conv.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename(conv.id)
              if (e.key === 'Escape') setRenamingId(null)
            }}
            style={{
              width: '100%',
              padding: '8px 10px',
              fontSize: 13,
              borderRadius: 8,
              border: `1px solid ${agentColors.accent}`,
              background: 'rgba(255,255,255,0.85)',
            }}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              borderRadius: 8,
              minHeight: 52,
              background: active ? 'rgba(245,240,235,0.75)' : 'transparent',
              border: active ? `1px solid ${agentColors.borderHero}` : '1px solid transparent',
            }}
            onMouseEnter={(e) => {
              const btn = e.currentTarget.querySelector('[data-menu-btn]')
              if (btn) btn.style.opacity = '1'
            }}
            onMouseLeave={(e) => {
              const btn = e.currentTarget.querySelector('[data-menu-btn]')
              if (btn && !showMenu) btn.style.opacity = '0'
            }}
          >
            <button
              type="button"
              onClick={() => onSelect(conv.id)}
              style={{
                flex: 1,
                textAlign: 'left',
                padding: '10px 10px 10px 12px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                minWidth: 0,
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: active ? 600 : 400,
                  color: agentColors.textPrimary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.35,
                }}
              >
                {active ? '● ' : ''}
                {conv.title || 'Untitled'}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: agentColors.textMuted,
                  marginTop: 2,
                  minHeight: 14,
                  lineHeight: 1.2,
                }}
              >
                {formatRelativeTime(conv.updated_at || conv.created_at)}
              </div>
            </button>
            <button
              type="button"
              data-menu-btn
              aria-label="Thread options"
              onClick={(e) => {
                e.stopPropagation()
                setMenuId(showMenu ? null : conv.id)
              }}
              style={{
                opacity: showMenu ? 1 : 0,
                border: 'none',
                background: 'transparent',
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: 16,
                color: agentColors.textSecondary,
                lineHeight: 1,
              }}
            >
              ⋯
            </button>
          </div>
        )}
        {showMenu ? (
          <AgentThreadMenu
            onRename={() => startRename(conv)}
            onDelete={() => {
              setMenuId(null)
              onDelete(conv.id)
            }}
            onClose={() => setMenuId(null)}
          />
        ) : null}
      </div>
    )
  }

  return (
    <div
      style={{
        width: mobile ? '100%' : 240,
        flexShrink: 0,
        borderRight: mobile ? 'none' : undefined,
        ...(!mobile ? glassRailStyle : {}),
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '12px 12px 8px' }}>
        <button
          type="button"
          onClick={onNewChat}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px dashed rgba(255,255,255,0.65)',
            background: 'rgba(255,255,255,0.35)',
            backdropFilter: 'blur(8px)',
            fontSize: 13,
            fontWeight: 600,
            color: agentColors.accent,
            cursor: 'pointer',
          }}
        >
          + New chat
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 12px' }}>
        {loading ? (
          <>
            <div
              style={{
                fontSize: 13,
                color: agentColors.textMuted,
                padding: '8px 12px 4px',
              }}
            >
              Loading Chat Threads…
            </div>
            <ThreadSkeletonRows />
          </>
        ) : sorted.length === 0 ? (
          <div style={{ fontSize: 13, color: agentColors.textMuted, padding: 12 }}>No chats yet</div>
        ) : (
          sorted.map(renderRow)
        )}
      </div>
    </div>
  )
}
